import { AgGridEvent, ColDef, ColumnState, GridOptions, ValueFormatterParams } from 'ag-grid-community';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { ModuleManager } from 'src/shared/models/module-manager';
import { ModuleManagerExportFormat, ModuleManagerRefreshType } from 'src/shared/models/module-manager/module-manager-options.model';
import { BancoItauDataCollection } from './banco-itau.model';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { BancoItauService } from './banco-itau.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ModuleManagerState } from 'src/shared/models/module-manager/module-manager-state.model';
import { DatePipe } from '@angular/common';
import { Subscription } from 'rxjs';
import { AgContextMenuService } from 'src/AngularJS/Reusables/ag-context-menu.ajs.service';
import { GridHelperOptions } from 'src/shared/models/ag-grid-helper.models';
import { AgGridAngular } from 'ag-grid-angular';

@Component({
    selector: 'cv-banco-itau',
    templateUrl: './banco-itau.component.html',
    styleUrls: ['./banco-itau.component.scss']
})
export class BANCOITAUComponent implements OnInit {
    @ViewChild('bancoItauWidgetGrid') agGrid: AgGridAngular;
    @Input() moduleManager: ModuleManager;
    @Input() moduleKey: string;

    public actualFilter = '';
    public filterValue = ""; // This variable contains the contents of the search bar.
    public showFilter = false;
    public isDataLoading = false;
    public isFirstLoad = false;
    public data: BancoItauDataCollection = null; // A copy of data returned by loadData will be stored here.


    // Ag-grid configuration:
    public gridOptions: GridOptions = null;
    private gridState: any = null;
    public noData = false;
    private stateSaveTimer: number;
    private setupGridTimer: number;
    private loadedColumnState: any;
    private columnDefs: ColDef[] = null;

    //For cxone search placeholder localization
    searchPlaceholder = "";
    localeText = {}; //Object holds localizations for ag grid

    private get isResizeWaiting() { return this.moduleManager?.container.isResizeWaiting; };

    constructor(
        private agGridHelperService: AgGridHelperService,
        private cvLocalizationService: CVLocalizationService,
        private service: BancoItauService,
        private datePipe: DatePipe,
    ) {
        // create a single bound callback function instead of a function for each event
        const queueStateSave = () => this.queueStateSave();

        this.gridOptions = this.agGridHelperService.createGridOptions({
            columnDefs: this.setupGrid(),
            rowData: [],
            onSortChanged: queueStateSave,
            onFilterChanged: queueStateSave,
            onColumnMoved: queueStateSave,
            onColumnPinned: queueStateSave,
            onColumnResized: queueStateSave,
            onColumnVisible: queueStateSave,
            onColumnRowGroupChanged: queueStateSave,
            onGridSizeChanged: event => {
                if (this.isResizeWaiting) {
                    this.agGridHelperService.sizeColumnsSmart(event);
                    this.queueStateSave();
                }
            },
        },
            {
                enableCopy: true
            } as GridHelperOptions);
    }

    ngOnInit(): void {
        this.initializeComponentAsync();
    }

    async initializeComponentAsync() {
        this.setupGrid();
        this.moduleManager.setMaxDateRange(14);
        this.moduleManager.showLoader = true;

        // Module Configuration:
        this.moduleManager.setOptions({
            name: 'Banco Itau', // sets the title of the module (defaults to the database entry's ModuleName, if unset).
            enableWidthClass: false, // enabling this will add mediaquery-like classes to 'moduleResponsiveContainer'
            widgetMode: false, // enabling this disables the module header and moves the menu to the body of the module
            onSettingsApplied: () => {
                this.loadData(false); // for example, call your data loading function once settings have been loaded
            },
            hasRefresh: true,
            refreshType: ModuleManagerRefreshType.Auto,
            onRefresh: isAutoRefresh => {
                if (!isAutoRefresh)
                    this.moduleManager.showLoader = true;
                return this.loadData(isAutoRefresh);
            },
            canRefresh: () => !this.isDataLoading, // determines whether a module's refresh menu item is clickable.  Can't refresh when there's already one in progress.
            onExport: format => this.service.getExport(this.moduleKey, format),
        });

        // Example header item.  Usually used for the magnifying glass for the search bar.
        this.moduleManager.addHeaderItem({
            name: "Search",
            icon: "search",
            click: () => this.toggleOpenSearch(),
        });

        // To save a state: call this.moduleManager.saveState() and all state values will be saved
        this.moduleManager.addState({
            name: "gridState",
            getStateData: () => this.loadedColumnState,
            onStateLoaded: stateData => {
                if (!stateData) {
                    return;
                }
                this.loadedColumnState = stateData;
                if (this.agGrid?.api && this.loadedColumnState)
                    this.agGridHelperService.loadGridState(this.agGrid?.api, stateData);
            },
            // if autoload is true, this state will start loading immediately
            //  and call onStateLoaded() when done
            // to load manually set to false and call this.moduleManager.loadState("sortedColumn");
            autoLoad: true
        });

        const moduleColumnState: ModuleManagerState<ColumnState[]> = {
            name: 'columnState',
            autoLoad: true,
            getStateData: () => {
                if (!this.agGrid?.api)
                    throw Error("Not ready to save state."); // throw because we are not ready to save state

                return this.agGrid?.api.getColumnState();
            },
            onStateLoaded: stateData => {
                if (stateData == null)
                    return;

                this.loadedColumnState = stateData;

                if (this.agGrid?.api) {
                    if (this.columnDefs && this.service.upgradeColumnStates(this.columnDefs, this.loadedColumnState))
                        this.moduleManager.saveState('columnState');
                    this.agGrid?.api.applyColumnState({ state: this.loadedColumnState });
                }
            }
        };

        this.moduleManager.addState(moduleColumnState);

        this.agGridHelperService.loadGridState(this.agGrid?.api, this.loadedColumnState)
    }

    /**
     * Called when the clear button on the filter box is clicked.  Clears the filter
     */
    public clearFilter() {
        this.filterValue = "";
        this.applyFilter();
    }

    // Called by AngularJS when the search bar's value has changed.
    public applyFilter() {
        if (this.agGrid?.api) { // notify ag-grid that the filter changed
            this.actualFilter = this.filterValue;
            this.actualFilter = this.actualFilter.trim();
            this.agGrid?.api.setGridOption('quickFilterText', this.actualFilter);
            if (this.showFilter && this.agGrid?.api?.getDisplayedRowCount() === 0 && this.filterValue.length !== 0) {
                this.agGrid?.api?.showNoRowsOverlay();
            }
            else {
                this.agGrid?.api?.hideOverlay();
            }
        }
    }

    private async loadData(isAutoRefresh) {
        if (this.isDataLoading)
            return; // don't have multiple outgoing requests
        this.isDataLoading = true;

        if (!isAutoRefresh)
            this.moduleManager.showLoader = true;

        try {
            this.data = await this.service.loadData(this.moduleKey, isAutoRefresh, this.isFirstLoad) as BancoItauDataCollection;
            if (this.agGrid?.api) {
                this.agGrid?.api.setGridOption('rowData', this.data.BancoItauDatas);
            }
            // else, the grid isn't ready, but the onGridReady() event handler will load up its initial values.

            this.moduleManager.showLoader = false;
            this.moduleManager.setAutoRefreshReady();
        } catch (err) {
            this.moduleManager.handleLoadError(err, isAutoRefresh);
        } finally {
            this.isDataLoading = false;
        }

        this.updateGrid();
        this.isFirstLoad = false;
    }

    private setupGrid() {
        return [
            {
                field: "ContactID",
                colId: "ContactID",
                headerName: "Contact ID",
                menuTabs: [],
                resizable: true,
            },
            {
                field: "CpfProposta",
                colId: "CpfProposta",
                headerName: "cpf_proposta",
                menuTabs: [],
                resizable: true,
            },
            {
                field: "ANI",
                colId: "ANI",
                headerName: "ANI",
                menuTabs: [],
                resizable: true,
            },
            {
                field: "Agent",
                colId: "Agent",
                headerName: "Agent",
                menuTabs: [],
                resizable: true,
            },
            {
                field: "CallDate",
                colId: "CallDate",
                headerName: "Call Date",
                menuTabs: [],
                resizable: true,
                valueFormatter: params => this.datePipe.transform(params.value, CVAngularDateTimeFormat), //window['CVAngularDateTimeFormat']
            },
            {
                field: "QueueTime",
                colId: "QueueTime",
                headerName: "Queue Time",
                menuTabs: [],
                resizable: true,
            },
            {
                field: "Skill",
                colId: "Skill",
                headerName: "Skill",
                menuTabs: [],
                resizable: true,
            },
            {
                field: "Campaign",
                colId: "Campaign",
                headerName: "Campaign",
                menuTabs: [],
                resizable: true,
            },
            {
                field: "Company",
                colId: "Company",
                headerName: "Company",
                menuTabs: [],
                resizable: true,
            },
            {
                field: "PointOfContact",
                colId: "PointOfContact",
                headerName: "Point of Contact",
                menuTabs: [],
                resizable: true,
            },
            {
                field: "Team",
                colId: "Team",
                headerName: "Team",
                menuTabs: [],
                resizable: true,
            }
        ];
    }

    /**
     * Shows and hides the search/filter bar
     */
    private toggleOpenSearch() { // called by moduleManager.addHeaderItem for search bar.
        if (this.showFilter) {
            this.showFilter = false;
            this.clearFilter();
        } else {
            this.showFilter = true;
        }
    }

    /**
     * Saves the grid's state save, debounced to 500ms.
     */
    private queueStateSave() {
        // Cancel previous save attempt, since it hasn't been 500ms, yet.
        if (this.stateSaveTimer) {
            clearTimeout(this.stateSaveTimer);
            this.stateSaveTimer = null;
        }

        // Create a timer to save the state 500ms from now.
        this.stateSaveTimer = window.setTimeout(() => {
            this.stateSaveTimer = null;
            if (this.agGrid?.api)
                this.gridState = this.agGridHelperService.getGridState(this.agGrid?.api);
            this.moduleManager.saveState('columnState'); // This will save all moduleManager.addState definitions
        }, 500/*ms*/);
    }

    private updateGrid() {
        if (!this.data)
            return;

        if (this.agGrid?.api) {
            let state: ColumnState[];
            if (!this.isFirstLoad) { // capture state before resetting columns
                state = this.agGrid?.api.getColumnState();
            }

            if (this.isFirstLoad) {
                if (this.loadedColumnState == null) { // auto size columns if no state is loaded
                    this.agGrid?.api.autoSizeAllColumns();
                } else {
                    // attempt to migrate old states, if they were migrated then re-save them
                    if (this.service.upgradeColumnStates(this.columnDefs, this.loadedColumnState)) {
                        this.moduleManager.saveState('columnState');
                    }

                    // set state to loaded state
                    this.agGrid?.api.applyColumnState({ state: this.loadedColumnState });
                }
            }
        } else {

            // Cancel previous save attempt, since it hasn't been 500ms, yet.
            if (this.setupGridTimer) {
                clearTimeout(this.setupGridTimer);
                this.setupGridTimer = null;
            }

            // Create a timer to save the state 500ms from now.
            this.setupGridTimer = window.setTimeout(() => {
                this.setupGridTimer = null;
                this.agGrid?.api.setGridOption('rowData', this.data.BancoItauDatas);
            }, 500/*ms*/);
        }
    }

    // sets columns min-width to fit their content, then sets the column widths to fit the grid (will never go below min)
    // only used now when the 'smart' mode can't be used or there isn't an event to pass along
    sizeColumns() {
        if (!this.data) {
            return;
        }

        // helper function to size columns
        this.agGridHelperService.sizeColumns(this.agGrid?.api, this.gridOptions);
    };

    /** Sizes the columns for the grid in a way fills empty space, but allows large grids to still scroll */
    sizeColumnsSmart(event: AgGridEvent) {
        // If gridReady, or clientWidth or clientHeight missing, use the generic sizing. Otherwise (most cases), use the 'smart' sizing.
        if (event.type === "gridReady" || !event['clientWidth'] || !event['clientHeight'])
            this.sizeColumns();
        else
            this.agGridHelperService.sizeColumns(this.agGrid?.api, this.gridOptions);
    }

}
